import { coveragesService } from '@/services/coverages';
import type { Coverage } from '@/services/coverages/types';
import { planResourcesService } from '@/services/plan-resources';
import type { PlanResource } from '@/services/plan-resources/types';
import type { Proposal } from '@/services/proposals/types';
import { ProposalDetails } from '@/services/quotes/types';
import { STALE_TIME_FIVE_MINUTES_MS } from '@/shared/constants/query-stale-time.constants';
import { formatCurrency } from '@/shared/utils';
import { formatPhone } from '@/shared/utils/format-phone';
import { formatRegisterNumber } from '@/shared/utils/format-register-number';
import { formatZipcode } from '@/shared/utils/format-zipcode.util';
import { getQuoteStatus } from '@/shared/utils/get-quote-status..util';
import { downloadFromSignedUrl } from '@/shared/utils/signed-url.util';
import {
  Button,
  Link,
  Spinner,
  Text,
} from '@noorden-insurtech/alba-design-system';
import { useQuery } from '@tanstack/react-query';
import Decimal from 'decimal.js';
import { ArrowUpDown, Download, Mail, Phone, User } from 'lucide-react';
import { useMemo } from 'react';
import { isBusinessProductCode } from '../../../proposals/utils/proposal-category';
import { BusinessLocationsResume } from '../../../proposals/components/proposal-steps/business-locations-resume.component';
import { PremiumCard } from '../../../proposals/components/proposal-steps/premium-card.component';
import {
  ResumeGroup,
  ResumeItem,
} from '../../../proposals/components/proposal-steps/step-resume.component';
import { EditableCoverageItem } from '../../../quotes/components/editable-coverage-card.component';
import { CoverageCard } from '../../../quotes/components/quote-details.component';

import { useConstructionTypes } from '../../../quotes/hooks/homeowner/use-construction-types.hook';
import { usePlanResourcesByCode } from '../../../quotes/hooks/homeowner/use-plan-resources-by-code.hook';
import { usePropertyClassifications } from '../../../quotes/hooks/homeowner/use-property-classifications.hook';
import { usePropertyTypes } from '../../../quotes/hooks/homeowner/use-property-types.hook';

import { useProposalsGeneratePdf } from '../../../proposals/hooks/use-proposals-generate-pdf.hook';
import { useInspectionDocument } from '../../hooks/use-inspection-document.hook';

type EditableCoverageSection = {
  title: string;
  items: EditableCoverageItem[];
};

interface Step1OverviewProps {
  inspectionId: string;
  proposalId: string;
  proposalDetails: ProposalDetails | undefined;
  isLoading: boolean;
  error: Error | null;
}

function normalizeInspectionContact(raw: unknown): {
  name: string;
  email: string;
  phone: string;
} {
  const c = raw as Record<string, unknown> | null | undefined;
  if (!c) return { name: '', email: '', phone: '' };

  const name =
    (c.name as string | undefined) ??
    (c.Name as string | undefined) ??
    (c.nome as string | undefined) ??
    (c.Nome as string | undefined) ??
    '';

  const email =
    (c.email as string | undefined) ?? (c.Email as string | undefined) ?? '';

  const phone =
    (c.phone as string | undefined) ??
    (c.Phone as string | undefined) ??
    (c.mobile as string | undefined) ??
    (c.Mobile as string | undefined) ??
    (c.cellphone as string | undefined) ??
    (c.Celular as string | undefined) ??
    (c.celular as string | undefined) ??
    (c.telefone as string | undefined) ??
    (c.Telefone as string | undefined) ??
    '';

  return {
    name: String(name ?? ''),
    email: String(email ?? ''),
    phone: String(phone ?? ''),
  };
}

export function Step1Overview({
  inspectionId: _inspectionId,
  proposalId: _proposalId,
  proposalDetails,
  isLoading,
  error,
}: Step1OverviewProps) {
  const { data: propertyTypes } = usePropertyTypes();
  const { data: constructionTypes } = useConstructionTypes();
  const { data: propertyClassifications } = usePropertyClassifications();
  const { data: inspectionDocument } = useInspectionDocument(
    proposalDetails?.policyId
  );
  const { mutate: generateProposalPdf, isPending: isGeneratingProposalPdf } =
    useProposalsGeneratePdf(proposalDetails?.policyId ?? '');

  // Buscar catálogo de coberturas
  const versionDate = '2025-11-03';
  const { data: coveragesCatalog } = useQuery<Coverage[]>({
    queryKey: ['product-coverages', proposalDetails?.productCode, versionDate],
    enabled: Boolean(proposalDetails?.productCode),
    staleTime: STALE_TIME_FIVE_MINUTES_MS,
    queryFn: async () => {
      if (!proposalDetails?.productCode) {
        throw new Error('ProductCode não encontrado');
      }
      const result = await coveragesService.getCoverages(
        proposalDetails.productCode,
        versionDate
      );
      if (!result.success || !result.data) {
        throw new Error(result.error || 'Falha ao buscar coberturas');
      }
      return result.data;
    },
  });

  const { data: assistancePlans } = useQuery<PlanResource[]>({
    queryKey: ['product-assistances', proposalDetails?.productId],
    enabled: Boolean(proposalDetails?.productId),
    staleTime: STALE_TIME_FIVE_MINUTES_MS,
    queryFn: async () => {
      if (!proposalDetails?.productId) {
        throw new Error('ProductId não encontrado');
      }

      const result = await planResourcesService.listByProduct(
        String(proposalDetails.productId)
      );

      if (!result.success || !result.data) {
        throw new Error(result.error || 'Falha ao buscar assistências');
      }

      return result.data;
    },
  });

  // Calcular valores resolvidos (usar defaults se proposalDetails não estiver disponível)
  const resolvedGrossPremium =
    proposalDetails?.grossPremium ??
    proposalDetails?.GrossPremium ??
    proposalDetails?.DuePremium ??
    0;
  const resolvedNetPremium = proposalDetails?.rawPremium;
  const resolvedIof = proposalDetails?.iof ?? proposalDetails?.IOF ?? 0;
  const resolvedPisCofins = proposalDetails?.pisCofins ?? 0;

  // Todos os useMemo devem ser chamados antes dos early returns
  const interestValue = useMemo(() => {
    return formatCurrency(resolvedPisCofins);
  }, [resolvedPisCofins]);

  const interestPercentage = useMemo(() => {
    if (resolvedPisCofins === 0 || !resolvedGrossPremium) return '0,00%';
    const percentage = new Decimal(resolvedPisCofins)
      .div(resolvedGrossPremium)
      .times(100)
      .toNumber();
    return `${percentage.toFixed(2).replace('.', ',')}%`;
  }, [resolvedGrossPremium, resolvedPisCofins]);

  const inspectionContacts = useMemo(() => {
    const contactsFromFirstRisk =
      proposalDetails?.lobs?.[0]?.risks?.[0]?.inspectionContacts;

    const allContacts =
      proposalDetails?.lobs?.flatMap(lob =>
        (lob.risks ?? []).flatMap(risk => risk.inspectionContacts ?? [])
      ) ?? [];

    const contacts =
      Array.isArray(contactsFromFirstRisk) && contactsFromFirstRisk.length > 0
        ? contactsFromFirstRisk
        : allContacts;

    if (!Array.isArray(contacts) || contacts.length === 0) return [];

    return contacts.map((rawContact, index) => {
      const contact = normalizeInspectionContact(rawContact);
      return { contact, index };
    });
  }, [proposalDetails?.lobs]);

  const coveragesEditable: EditableCoverageSection[] = useMemo(() => {
    if (!proposalDetails) {
      return [];
    }

    const coveragesFromApi =
      proposalDetails?.lobs?.[0]?.risks?.[0]?.coverages || [];

    const resolveCoverageLabel = (code?: string) => {
      const resolvedCode = (code || '').trim();
      if (!resolvedCode) return 'Cobertura';
      const match = coveragesCatalog?.find(cov => cov.code === resolvedCode);
      return match?.name?.trim() || resolvedCode;
    };

    // Filtrar pelo código de produto (ASSIST/CAPITA) antes de mapear: na
    // listagem de Coberturas eles aparecem como "Assistências" depois do
    // lookup no catálogo, então um filtro por description duplicava o item
    // (uma vez para ASSIST e outra para CAPITA) já que ambos resolvem para
    // o mesmo rótulo. As assistências são exibidas no card próprio abaixo.
    const isAssistanceOrCapitaCode = (code?: string) => {
      const upper = (code || '').trim().toUpperCase();
      return upper === 'ASSIST' || upper === 'CAPITA';
    };

    const items: EditableCoverageItem[] = coveragesFromApi
      .filter(coverage => !isAssistanceOrCapitaCode(coverage.productElementCode))
      .map(coverage => ({
        description: resolveCoverageLabel(coverage.productElementCode),
        limiteLabel: 'Limite Máximo de Indenização*',
        limitePlaceholder: 'R$ 000,00',
        limiteValue: formatCurrency(coverage?.sumInsured ?? 0),
        premioLabel: 'Franquia*',
        premioPlaceholder:
          'Franquia de 1%  Com limite entre R$ 0,01 e R$ 1.000',
        premioValue: formatCurrency(coverage?.deductible ?? 0),
      }));

    return [
      {
        title: 'Coberturas',
        items,
      },
    ];
  }, [proposalDetails, coveragesCatalog]);

  const resolvedPlanCode = useMemo(() => {
    const coverages = proposalDetails?.lobs?.[0]?.risks?.[0]?.coverages || [];
    const assistanceCoverage = coverages.find(
      coverage => coverage.productElementCode === 'ASSIST'
    );

    if (assistanceCoverage?.planCode) {
      return assistanceCoverage.planCode;
    }

    const capitaCoverage = coverages.find(
      coverage => coverage.productElementCode === 'CAPITA'
    );

    return capitaCoverage?.planCode || '';
  }, [proposalDetails]);

  const { data: planResources } = usePlanResourcesByCode(
    resolvedPlanCode || undefined
  );

  const assistanceItems = useMemo(() => {
    if (!Array.isArray(planResources) || planResources.length === 0) {
      return [];
    }

    return planResources
      .filter(resource => {
        const code = String(resource.code || '')
          .trim()
          .toUpperCase();
        if (code === 'CAPITA') return false;

        const name = resource.name?.trim();
        if (!name) return false;

        const normalized = name.toLowerCase();
        if (
          normalized.includes('sorteio') ||
          normalized.includes('capitaliz')
        ) {
          return false;
        }

        return true;
      })
      .map(resource => resource.name?.trim())
      .filter((name): name is string => Boolean(name));
  }, [planResources]);

  const selectedPlanTitle = useMemo(() => {
    const planName = assistancePlans
      ?.find(plan => plan.code === resolvedPlanCode)
      ?.name?.trim();

    if (!planName) return 'Assistências';

    return planName.toLowerCase().startsWith('pacote ')
      ? planName
      : `Pacote ${planName}`;
  }, [assistancePlans, resolvedPlanCode]);

  if (isLoading) {
    return (
      <div className='flex items-center justify-center py-12'>
        <Spinner size='lg' colorScheme='blue' />
      </div>
    );
  }

  if (error) {
    return (
      <div className='flex flex-col items-center justify-center gap-4 py-12'>
        <Text className='text-alba-error-red-500'>
          Erro ao carregar detalhes da proposta
        </Text>
        <Text className='text-alba-gray-600 text-sm'>
          {error instanceof Error ? error.message : 'Erro desconhecido'}
        </Text>
      </div>
    );
  }

  if (!proposalDetails) {
    return (
      <div className='flex items-center justify-center py-12'>
        <Text className='text-alba-gray-600'>
          Nenhum dado encontrado para esta proposta
        </Text>
      </div>
    );
  }

  const isBusinessProposal = isBusinessProductCode(proposalDetails?.productCode);

  const totalValue = formatCurrency(resolvedGrossPremium);
  const liquidValue =
    resolvedNetPremium != null ? formatCurrency(resolvedNetPremium) : '-';
  const iofValue = resolvedIof;
  const product = isBusinessProposal ? 'Empresarial' : 'Residencial';
  const proposalNumber = proposalDetails?.proposalNo ?? '';

  const propertyType =
    propertyTypes?.find(
      pt =>
        String(pt.code) === proposalDetails?.lobs?.[0]?.risks?.[0]?.propertyType
    )?.name || '';

  const constructionType =
    constructionTypes?.find(
      ct =>
        String(ct.code) ===
        proposalDetails?.lobs?.[0]?.risks?.[0]?.constructionType
    )?.description || '';

  const propertyClassification =
    propertyClassifications?.find(
      pc =>
        String(pc.code) ===
        proposalDetails?.lobs?.[0]?.risks?.[0]?.propertyClassification
    )?.description || '';

  return (
    <div className='flex flex-col gap-8'>
      <div className='flex flex-col gap-6 rounded-2xl bg-white px-4 py-6 shadow-xl sm:gap-8 sm:px-8 sm:py-12'>
        <PremiumCard
          totalValue={totalValue}
          liquidValue={liquidValue}
          interestPercentage={interestPercentage}
          interestValue={interestValue}
          iofValue={formatCurrency(iofValue || 0)}
          product={product}
          proposalNumber={proposalNumber}
          status={
            getQuoteStatus(Number(proposalDetails?.policyStatus ?? 1)).label
          }
          showPisCofins={false}
        />

        {/* Informações da Inspeção */}
        <div className='flex flex-col gap-6'>
          <p className='font-montserrat text-base font-normal text-alba-black-500'>
            Informações da Inspeção
          </p>

          <div className='flex flex-col gap-4'>
            <p className='font-montserrat text-sm font-bold text-alba-black-500'>
              Contato para Inspeção
            </p>

            <div className='hidden overflow-hidden rounded-3xl bg-alba-white-500 shadow-[0px_4px_24px_rgba(15,22,30,0.08)] md:flex md:flex-col'>
              {/* Cabeçalho da tabela */}
              <div className='bg-alba-gray-200 rounded-t-3xl px-6 py-4'>
                <div className='flex flex-row items-center gap-4'>
                  <div className='flex flex-1 items-center gap-2'>
                    <span className='font-montserrat text-xs font-bold text-alba-black-500'>
                      Nome
                    </span>
                    <ArrowUpDown
                      className='h-4 w-4 text-alba-gray-500'
                      strokeWidth={1.5}
                    />
                  </div>
                  <div className='flex flex-1 items-center gap-2'>
                    <span className='font-montserrat text-xs font-bold text-alba-black-500'>
                      E-mail
                    </span>
                    <ArrowUpDown
                      className='h-4 w-4 text-alba-gray-500'
                      strokeWidth={1.5}
                    />
                  </div>
                  <div className='flex flex-1 items-center gap-2'>
                    <span className='font-montserrat text-xs font-bold text-alba-black-500'>
                      Telefone
                    </span>
                    <ArrowUpDown
                      className='h-4 w-4 text-alba-gray-500'
                      strokeWidth={1.5}
                    />
                  </div>
                </div>
              </div>

              {/* Linhas da tabela */}
              {inspectionContacts.length > 0 ? (
                inspectionContacts.map(({ contact, index }) => (
                  <div
                    key={index}
                    className='flex flex-row items-center gap-4 px-6 py-4 border-t border-alba-gray-300'
                  >
                    <div className='flex flex-1 items-center'>
                      <User
                        className='mr-2 h-4 w-4 shrink-0 text-alba-black-500'
                        strokeWidth={1.5}
                      />
                      <span className='font-montserrat text-sm font-normal text-alba-black-500'>
                        {contact.name || '-'}
                      </span>
                    </div>
                    <div className='flex flex-1 items-center gap-2'>
                      <Mail
                        className='h-4 w-4 shrink-0 text-alba-black-500'
                        strokeWidth={1.5}
                      />
                      <span className='font-montserrat text-sm font-normal text-alba-black-500'>
                        {contact.email || '-'}
                      </span>
                    </div>
                    <div className='flex flex-1 items-center gap-2'>
                      <Phone
                        className='h-4 w-4 shrink-0 text-alba-black-500'
                        strokeWidth={1.5}
                      />
                      <span className='font-montserrat text-sm font-normal text-alba-black-500'>
                        {(() => {
                          const formatted = formatPhone(contact.phone || '');
                          return formatted === 'N/A' ? '-' : formatted;
                        })()}
                      </span>
                    </div>
                  </div>
                ))
              ) : (
                <div className='flex flex-row items-center gap-4 px-6 py-4 border-t border-alba-gray-300'>
                  <div className='flex flex-1 items-center'>
                    <span className='font-montserrat text-sm font-normal text-alba-gray-500'>
                      -
                    </span>
                  </div>
                  <div className='flex flex-1 items-center gap-2'>
                    <Mail
                      className='h-4 w-4 shrink-0 text-alba-gray-400'
                      strokeWidth={1.5}
                    />
                    <span className='font-montserrat text-sm font-normal text-alba-gray-500'>
                      -
                    </span>
                  </div>
                  <div className='flex flex-1 items-center gap-2'>
                    <Phone
                      className='h-4 w-4 shrink-0 text-alba-gray-400'
                      strokeWidth={1.5}
                    />
                    <span className='font-montserrat text-sm font-normal text-alba-gray-500'>
                      -
                    </span>
                  </div>
                </div>
              )}
            </div>

            <div className='space-y-3 md:hidden'>
              {inspectionContacts.length > 0 ? (
                inspectionContacts.map(({ contact, index }) => (
                  <article
                    key={`inspection-contact-mobile-${index}`}
                    className='rounded-2xl border border-alba-gray-300 bg-white p-3'
                  >
                    <div className='flex items-center gap-2'>
                      <User
                        className='h-4 w-4 shrink-0 text-alba-black-500'
                        strokeWidth={1.5}
                      />
                      <p className='text-sm font-semibold text-alba-black-500'>
                        {contact.name || '-'}
                      </p>
                    </div>
                    <div className='mt-2 flex items-center gap-2'>
                      <Mail
                        className='h-4 w-4 shrink-0 text-alba-black-500'
                        strokeWidth={1.5}
                      />
                      <span className='break-all text-sm text-alba-black-500'>
                        {contact.email || '-'}
                      </span>
                    </div>
                    <div className='mt-1 flex items-center gap-2'>
                      <Phone
                        className='h-4 w-4 shrink-0 text-alba-black-500'
                        strokeWidth={1.5}
                      />
                      <span className='text-sm text-alba-black-500'>
                        {(() => {
                          const formatted = formatPhone(contact.phone || '');
                          return formatted === 'N/A' ? '-' : formatted;
                        })()}
                      </span>
                    </div>
                  </article>
                ))
              ) : (
                <div className='rounded-2xl border border-alba-gray-300 bg-white p-3 text-sm text-alba-gray-500'>
                  Nenhum contato de inspeção informado.
                </div>
              )}
            </div>
          </div>
        </div>

        {/* Informações da Proposta */}
        <div className='flex flex-col gap-6'>
          <p className='font-montserrat text-base font-normal text-alba-black-500'>
            Informações da Proposta
          </p>

          <div className='grid grid-cols-1 gap-4 md:grid-cols-2 md:gap-6'>
            {!isBusinessProposal && (
              <ResumeGroup title='Dados do Risco'>
                <ResumeItem
                  label='CEP'
                  value={formatZipcode(
                    proposalDetails?.lobs?.[0]?.risks?.[0]?.zipCode || ''
                  )}
                />
                <ResumeItem label='Tipo do Imóvel' value={propertyType} />
                <ResumeItem label='Tipo de Construção' value={constructionType} />
                <ResumeItem
                  label='Classificação do Imóvel'
                  value={propertyClassification}
                />
              </ResumeGroup>
            )}

            <ResumeGroup title='Dados do Proponente'>
              <ResumeItem
                label='CPF'
                value={formatRegisterNumber(
                  proposalDetails?.customer?.cpf ||
                  proposalDetails?.customer?.idNo ||
                  ''
                )}
              />
              <ResumeItem
                label='Nome'
                value={proposalDetails?.customer?.name || ''}
              />
              <ResumeItem
                label='Email'
                value={proposalDetails?.customer?.email || ''}
              />
              <ResumeItem
                label='Telefone'
                value={(() => {
                  const formatted = formatPhone(
                    proposalDetails?.customer?.cellphone ||
                    proposalDetails?.customer?.phone ||
                    ''
                  );
                  return formatted === 'N/A' ? '-' : formatted;
                })()}
              />
            </ResumeGroup>

            {isBusinessProposal && (
              <BusinessLocationsResume
                proposal={proposalDetails as unknown as Proposal}
              />
            )}
          </div>
        </div>

        {/* PDFs */}
        <div className='flex flex-col gap-4 sm:flex-row sm:gap-6'>
          <div className='flex flex-col gap-2'>
            <p className='font-montserrat text-sm font-bold text-alba-black-500'>
              PDF da Cotação
            </p>
            <Button
              variant='secondary'
              size='md'
              className='w-full sm:w-auto'
              onClick={async () => {
                if (!inspectionDocument?.url) {
                  return;
                }
                try {
                  const filename =
                    inspectionDocument.key.split('/').pop() ||
                    `cotacao-${proposalDetails?.policyId}.pdf`;
                  await downloadFromSignedUrl(inspectionDocument.url, filename);
                } catch (error) {
                  console.error('Erro ao baixar PDF:', error);
                }
              }}
              disabled={!inspectionDocument?.url}
            >
              <Download className='w-4 h-4' />
              Ver PDF
            </Button>
          </div>

          <div className='flex flex-col gap-2'>
            <p className='font-montserrat text-sm font-bold text-alba-black-500'>
              PDF da Proposta
            </p>
            <Button
              variant='secondary'
              size='md'
              className='w-full sm:w-auto'
              onClick={() => {
                if (
                  !proposalDetails?.policyId ||
                  !proposalDetails?.productCode
                ) {
                  return;
                }
                generateProposalPdf({
                  productCode: proposalDetails.productCode,
                  stage: 'proposal',
                });
              }}
              disabled={!proposalDetails?.policyId || isGeneratingProposalPdf}
            >
              <Download className='w-4 h-4' />
              Ver PDF
            </Button>
          </div>
        </div>

        {/* Coberturas */}
        <div className='flex flex-col gap-6'>
          <p className='font-montserrat text-base font-normal text-alba-black-500'>
            Coberturas
          </p>

          <div className='flex flex-col gap-4'>
            <CoverageCard
              items={
                coveragesEditable
                  ?.find(coverage => coverage.title === 'Coberturas')
                  ?.items.map(item => ({
                    name: item.description,
                    value: item.limiteValue,
                  })) ?? []
              }
              defaultExpanded
            />

            <CoverageCard
              title={selectedPlanTitle}
              items={assistanceItems.map(item => ({
                name: item,
                value: '',
              }))}
            />
          </div>
        </div>
      </div>

      <div className='flex flex-row justify-between gap-4'>
        <Link href='/inspections'>
          <Button variant='ghost' size='md' className='w-full sm:w-auto'>
            Sair
          </Button>
        </Link>
      </div>
    </div>
  );
}
